import { useMemo, useState } from "react";
import { currency, customerName, ownerLabel, sortNewest } from "../lib/format.js";
import { ActivityTable } from "../components/crmTables.jsx";
import { CustomerLink, EmployeeLink } from "../components/recordLinks.jsx";
import { TeamTreeTable } from "../components/teamTreeTable.jsx";
import { Metric, Panel, ResourceKeyValue, ResourceTable, SelectField, StageChart, Topbar } from "../components/ui.jsx";

export function ReportsView({
  report,
  activities,
  customers,
  users,
  activityCustomFields = [],
  openCustomer,
  openEmployee,
  openCall,
  openVisit,
  openMessage,
  openTarget,
  openExpense,
}) {
  const [activityFilters, setActivityFilters] = useState({ userId: "", customerId: "" });
  const filteredActivities = useMemo(
    () =>
      activities.filter(
        (activity) =>
          (!activityFilters.userId || activity.userId === activityFilters.userId) &&
          (!activityFilters.customerId || activity.customerId === activityFilters.customerId),
      ),
    [activities, activityFilters],
  );
  const activityUserOptions = useMemo(() => {
    const activityUserIds = new Set(activities.map((activity) => activity.userId).filter(Boolean));
    return [
      { value: "", label: "All team members" },
      ...users
        .filter((user) => activityUserIds.has(user.id))
        .map((user) => ({ value: user.id, label: ownerLabel(users, user.id) })),
    ];
  }, [activities, users]);
  const activityCustomerOptions = useMemo(() => {
    const customerOptionsById = new Map();
    for (const activity of activities) {
      if (!activity.customerId || customerOptionsById.has(activity.customerId)) continue;
      customerOptionsById.set(activity.customerId, customerName(customers, activity.customerId) || activity.customerName || activity.customerId);
    }
    return [
      { value: "", label: "All customers" },
      ...[...customerOptionsById.entries()].map(([value, label]) => ({ value, label })),
    ];
  }, [activities, customers]);

  return (
    <>
      <Topbar title="Reports" subtitle="Phase 1 reports for manager review and sales discipline." />
      <section className="metric-grid">
        <Metric label="Calls Report" value={report?.calls ?? 0} />
        <Metric label="Visits Report" value={report?.visits ?? 0} />
        <Metric label="Calls / Visits" value={report?.callsVsVisits ?? 0} />
        <Metric label="Lost Leads" value={report?.lost ?? 0} />
      </section>
      <section className="content-grid">
        <Panel title="Conversion Summary">
          <div className="list">
            <div className="list-item">
              <strong>{report?.converted ?? 0}</strong>
              <span>Converted leads</span>
            </div>
            <div className="list-item">
              <strong>{report?.conversionRate ?? 0}%</strong>
              <span>Conversion rate</span>
            </div>
          </div>
        </Panel>
        <Panel title="Lead Stage Report">
          <StageChart stageCounts={report?.stageCounts ?? {}} />
        </Panel>
      </section>
      <section className="panel spaced">
        <h2>Activity Detail</h2>
        <div className="form-grid">
          <SelectField
            label="Team member"
            value={activityFilters.userId}
            options={activityUserOptions}
            secondaryActionLabel="Open selected employee"
            secondaryActionDisabled={!activityFilters.userId}
            onSecondaryAction={openEmployee ? () => openEmployee(activityFilters.userId) : undefined}
            onChange={(userId) => setActivityFilters((current) => ({ ...current, userId }))}
          />
          <SelectField
            label="Customer"
            value={activityFilters.customerId}
            options={activityCustomerOptions}
            secondaryActionLabel="Open selected prospect"
            secondaryActionDisabled={!activityFilters.customerId}
            onSecondaryAction={openCustomer ? () => openCustomer(activityFilters.customerId) : undefined}
            onChange={(customerId) => setActivityFilters((current) => ({ ...current, customerId }))}
          />
          <div className="field">
            <label>Visible activities</label>
            <div className="filter-summary">{filteredActivities.length} of {activities.length}</div>
          </div>
          <div className="modal-actions">
            <button className="ghost-btn" type="button" onClick={() => setActivityFilters({ userId: "", customerId: "" })}>
              Clear Filters
            </button>
          </div>
        </div>
        <ActivityTable
          activities={[...filteredActivities].sort(sortNewest)}
          customers={customers}
          users={users}
          activityCustomFields={activityCustomFields}
          openCustomer={openCustomer}
          openEmployee={openEmployee}
          openCall={openCall}
          openVisit={openVisit}
          openMessage={openMessage}
        />
      </section>
      <section className="panel spaced">
        <h2>Team Roll-Up Report</h2>
        <TeamTreeTable
          rows={report?.teamRollups ?? report?.fullReport?.teamRollupReport ?? []}
          onOpenEmployee={openEmployee}
          onAssignTarget={openTarget}
          onAddExpense={openExpense}
        />
      </section>
      <section className="content-grid spaced">
        <Panel title="Target vs Actual">
          <ResourceTable
            rows={report?.fullReport?.targetVsActualReport ?? []}
            columns={[
              ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
              ["productCategory", "Category"],
              ["period", "Period"],
              ["revenueTarget", "Target", currency],
              ["achievedRevenue", "Achieved", currency],
              ["pendingRevenueGap", "Gap", currency],
              ["revenueAchievementPercent", "%"],
            ]}
            action={openTarget || openExpense ? (row) => {
              const canUseRep = users.some((item) => item.id === row.userId && item.active !== false);
              return (
                <div className="row-actions">
                  {openTarget ? (
                    <button className="mini-btn" type="button" disabled={!canUseRep} onClick={() => openTarget(row, targetDraftFromReportRow(row))}>
                      Assign Target
                    </button>
                  ) : null}
                  {openExpense ? (
                    <button className="mini-btn" type="button" disabled={!canUseRep} onClick={() => openExpense(row, expenseDraftFromReportRow(row))}>
                      Add Expense
                    </button>
                  ) : null}
                </div>
              );
            } : null}
          />
        </Panel>
      </section>
      <section className="content-grid spaced">
        <Panel title="Product-wise Sales">
          <ResourceKeyValue data={report?.fullReport?.productWiseSalesReport ?? {}} formatter={currency} />
        </Panel>
        <Panel title="Area-wise Sales">
          <ResourceKeyValue data={report?.fullReport?.areaWiseSalesReport ?? {}} formatter={currency} />
        </Panel>
        <Panel title="Visit vs Recommendation">
          <ResourceTable
            rows={report?.fullReport?.visitVsRecommendationReport ?? []}
            columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["recommendations", "Recommendations"], ["geofenceStatus", "Geofence"]]}
            action={(row) => {
              const customer = customers.find((item) => item.id === row.customerId) || null;
              return (
                <div className="row-actions">
                  {openCall ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openCall(customer)}>
                      Call
                    </button>
                  ) : null}
                  {openVisit ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openVisit(customer)}>
                      Visit
                    </button>
                  ) : null}
                  {openMessage ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openMessage(customer, followUpMessageDraft(row))}>
                      Message
                    </button>
                  ) : null}
                </div>
              );
            }}
          />
        </Panel>
      </section>
      <section className="content-grid spaced">
        <Panel title="Follow-up Pending Report">
          <ResourceTable
            rows={report?.fullReport?.followUpPendingReport ?? []}
            columns={[["name", "Customer", (value, row) => <CustomerLink customerId={row.id || row.customerId} customers={customers} fallbackName={value} openCustomer={openCustomer} />], ["followUpDate", "Due"], ["status", "Status"], ["ownerId", "Owner", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />]]}
            action={(row) => {
              const customer = customerFromReportRow(row);
              return (
                <div className="row-actions">
                  {openCall ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openCall(customer)}>
                      Call
                    </button>
                  ) : null}
                  {openVisit ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openVisit(customer)}>
                      Visit
                    </button>
                  ) : null}
                  {openMessage ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openMessage(customer, followUpMessageDraft(row))}>
                      Message
                    </button>
                  ) : null}
                </div>
              );
            }}
          />
        </Panel>
        <Panel title="Support Report">
          <ResourceTable
            rows={report?.fullReport?.supportReport ?? []}
            columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["queryType", "Type"], ["priority", "Priority"], ["status", "Status"], ["slaDueDate", "SLA"]]}
            action={(row) => {
              const customer = customers.find((item) => item.id === row.customerId) || null;
              return (
                <div className="row-actions">
                  {openCall ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openCall(customer)}>
                      Call
                    </button>
                  ) : null}
                  {openVisit ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openVisit(customer)}>
                      Visit
                    </button>
                  ) : null}
                  {openMessage ? (
                    <button className="mini-btn" type="button" disabled={!customer} onClick={() => openMessage(customer, supportMessageDraft(row))}>
                      Message
                    </button>
                  ) : null}
                </div>
              );
            }}
          />
        </Panel>
      </section>
    </>
  );

  function customerFromReportRow(row) {
    const customerId = row.id || row.customerId;
    return customers.find((customer) => customer.id === customerId) || null;
  }
}

function targetDraftFromReportRow(row) {
  return {
    productCategory: row.productCategory || "All",
    period: row.period || "Monthly",
    revenueTarget: Number(row.revenueTarget || 0),
    quantityTarget: Number(row.quantityTarget || 0),
  };
}

function expenseDraftFromReportRow(row) {
  return {
    purpose: `${row.productCategory || "Target"} gap follow-up`,
  };
}

function followUpMessageDraft(row) {
  const customerLabel = row.name || row.customerName || "your requirement";
  const dueLabel = row.followUpDate ? ` due on ${row.followUpDate}` : "";
  return {
    channel: "WhatsApp",
    template: "Follow-up",
    message: `Following up on ${customerLabel}${dueLabel}.`,
  };
}

function supportMessageDraft(row) {
  const typeLabel = row.queryType || "support";
  const slaLabel = row.slaDueDate ? ` before SLA ${row.slaDueDate}` : "";
  return {
    channel: "WhatsApp",
    template: row.priority === "High" ? "Retention" : "Follow-up",
    message: `Following up on your ${typeLabel} request${slaLabel}.`,
  };
}
